import {
  TAddApplicationStepResponse,
  TAddStepRequest,
  TGetVacationApplicationsRequest,
  TUpdateStepRequest,
  TVacationSupervisersPaginated,
} from 'shared/types/vacation-schedule.types';
import { TVacationInfo } from 'shared/types/vacations.types';
import { DowntimesStatus, SortCondition, ZupRequestStatusType } from 'shared/enums';
import { edpApiV1 } from './index';

export type VacationApplicationRequest = {
  isArchive?: boolean;
  fio?: string;
  byId?: string;
  size?: number | null;
  page?: number | null;
  // order?: SortCondition;
  employeeId?: number;
  // divisionId?: string | null;
};

export type VacationApplicationInfo = {
  id: number;
  name: string;
  lastName: string;
  middleName: string;
  image: string | null;
  division: {
    id: string;
    name: string;
    code: string;
  };
  position: {
    id: string;
    name: string;
  };
  request: {
    id: number;
    type: DowntimesStatus;
    status: {
      name: string;
      type: string;
    };
    zupRequestStatus?: {
      name: string;
      type: string;
    };
    comment: string;
    endedAt: string;
    isUnpaid: boolean;
    startedAt: string;
    amountDays: number;
    vacationType: {
      id: string;
      name: string;
    };
    rejectionReason: string;
  };
  step: {
    depth: null | number;
    divisionId: null | string;
    divisionName: null | string;
    divisionCode: null | string;
  };
  amountSteps: number;
};

export type VacationApplicationResponse = {
  data: VacationApplicationInfo[] | null;
  pagination: {
    size: number;
    page: number;
    total: number;
  } | null;
};

export type TPreparedVacationApplicationInfo = {
  key: number;
  fio: string;
  vacationType: string;
  type: string;
  startedAt: string;
  endedAt: string;
  applyStep: string;
  amountDays: number;
  id: number;
  status: {
    type: string;
    text: string;
    isSupervisor?: boolean;
  };
  created?: string;
  zupRequestStatus?: {
    type: ZupRequestStatusType;
    name: string;
  } | null;
  hr?: { id: number; name: string; lastName: string; middleName: string } | null;
};

export type ChangeResponsible = {
  requestId?: number | string;
  hrId: number;
  employeeId: number;
};

export type RejectVacationRequest = {
  requestId?: number;
  rejectionReason: string;
  employeeId: number;
};

export const vacationApplicationApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    getSteps: build.mutation<TAddApplicationStepResponse, TAddStepRequest>({
      query: body => ({ url: '/v1/admin/hr/vacation-application/agreements-depth', method: 'POST', body }),
      invalidatesTags: ['vacationApplicationSupervisors'],
    }),

    updateSteps: build.mutation<TAddApplicationStepResponse, TUpdateStepRequest>({
      query: body => ({ url: '/v1/admin/hr/vacation-application/agreements-depth', method: 'PATCH', body }),
      invalidatesTags: ['vacationApplicationSupervisors'],
    }),

    getDivisionsSupervisors: build.query<TVacationSupervisersPaginated, TGetVacationApplicationsRequest>({
      query: params => ({
        url: '/v1/admin/hr/vacation-application/division-agreements-depth',
        method: 'GET',
        params,
      }),
      providesTags: ['vacationApplicationSupervisors'],
    }),
    vacationsApplicationVA: build.query<VacationApplicationResponse, VacationApplicationRequest>({
      query: payload => ({
        url: `/v1/admin/vacation-application/subordinates/requests`,
        method: 'GET',
        params: payload,
      }),
      providesTags: ['vacationApplicationVacation'],
    }),
    singleVacationVA: build.query<VacationApplicationInfo, { requestId: number; employeeId: number }>({
      query: ({ requestId, employeeId }) => ({
        url: `/v1/admin/hr/vacation-application/subordinates/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['vacationApplicationVacation'],
    }),
    changeResponsibleVA: build.mutation<unknown, ChangeResponsible>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/admin/hr/vacation-application/requests/subordinates/${employeeId}/${requestId}/change-responsible`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['vacationApplicationVacation'],
    }),
    approveVacationVA: build.mutation<unknown, { reqiestId: number; employeeId: number }>({
      query: ({ reqiestId, employeeId }) => ({
        url: `/v1/admin/hr/vacation-application/subordinates/${employeeId}/requests/${reqiestId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['vacationApplicationVacation'],
    }),
    rejectVacationVA: build.mutation<unknown, RejectVacationRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/admin/hr/vacation-application/subordinates/${employeeId}/requests/${requestId}/reject`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['vacationApplicationVacation'],
    }),
    takeToWorkVA: build.mutation<unknown, ChangeResponsible>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/admin/hr/vacation-application/subordinates/${employeeId}/requests/${requestId}/take-to-work`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['vacationApplicationVacation'],
    }),
    vacationDownloadVA: build.query<string, { type: string }>({
      query: payload => ({
        url: '/v1/admin/hr/vacation-application/requests/download-file',
        method: 'GET',
        params: payload,
        responseHandler: async response => {
          const link = document.createElement('a');
          link.download = 'vacations';
          link.href = window.URL.createObjectURL(await response.blob());
          link.click();
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDivisionsSupervisorsQuery,
  useLazyGetDivisionsSupervisorsQuery,
  useUpdateStepsMutation,
  useGetStepsMutation,
  useApproveVacationVAMutation,
  useChangeResponsibleVAMutation,
  useLazySingleVacationVAQuery,
  useLazyVacationDownloadVAQuery,
  useRejectVacationVAMutation,
  useSingleVacationVAQuery,
  useTakeToWorkVAMutation,
  useVacationDownloadVAQuery,
  useLazyVacationsApplicationVAQuery,
  useVacationsApplicationVAQuery,
} = vacationApplicationApi;
